import { Lock } from 'lucide-react'
import clsx from 'clsx'
import styles from './Infrastructure.module.css'

export interface InfoItem {
  label: string
  value: string
  mono?: boolean
}

interface ReadOnlyInfoCardProps {
  title: string
  items: InfoItem[]
  /** One line of explanation under the values, for example how a generated name was derived. */
  note?: string
  loading?: boolean
  /** A message when the values could not be loaded. */
  error?: string | null
  onRetry?: () => void
}

/**
 * Values the user cannot edit: things fetched from existing infrastructure, or generated names. They are shown in a shaded,
 * dashed card with a "Read-only" badge so they can never be mistaken for an input.
 */
export function ReadOnlyInfoCard({ title, items, note, loading, error, onRetry }: ReadOnlyInfoCardProps) {
  return (
    <section className={styles.readOnly} aria-label={title}>
      <div className={styles.readOnlyHeader}>
        <h5 className={styles.readOnlyTitle}>{title}</h5>
        <span className={styles.readOnlyBadge}>
          <Lock size={10} aria-hidden="true" style={{ marginRight: 4, verticalAlign: '-1px' }} />
          Read-only
        </span>
      </div>
      {loading ? (
        <span className={styles.status} aria-live="polite">Loading {title.toLowerCase()}…</span>
      ) : error ? (
        <div role="alert" className={styles.alert}>
          <span>{error}</span>
          {onRetry && (
            <button type="button" className={styles.linkButton} onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      ) : (
        <dl className={styles.readOnlyList}>
          {items.map((item) => (
            <div key={item.label} style={{ display: 'contents' }}>
              <dt>{item.label}</dt>
              <dd className={clsx(item.mono && styles.mono)}>{item.value || '—'}</dd>
            </div>
          ))}
        </dl>
      )}
      {note && !loading && !error && <p className={styles.note}>{note}</p>}
    </section>
  )
}
